import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import useMediaQuery from '@mui/material/useMediaQuery';
// Redux 

import { selectFile, selectFolder, setConfirmedDraggedFile, setIsDropdownOpened, setMouseInfo, setUnconfirmedDraggedFile } from '../../../../redux/features/doctor/Dashboard/documents/fileExplorer';

// Components 

import NavigationColumn from './folders/NavigationColumn';
import FilesSection from './filesList/FilesSection';
import Box from '@mui/material/Box';
import CorrectFile from './actions/correctFile/CorrectFile';
import GeneralDropdown from './actions/dropdowns/GeneralDropdown';
import Consultations from './consultations/Consultations';
import Trash from './trash/Trash';

interface Props { 
    extraStyle?: any;
}; 

const FileExplorer: React.FC<Props> = ({ extraStyle }) => {

    const dispatch = useAppDispatch(); 
    const isMobile = useMediaQuery('(max-width:768px)'); // Mobile breakpoint

    // Sidebar width calculation logic (consistent with other components)
    const sidebarMinWidth = useMediaQuery('(max-width:600px)') ? 70 : 125;
    const [leftOffset, setLeftOffset] = useState(`${sidebarMinWidth}px`);
    const [calculatedWidth, setCalculatedWidth] = useState(`calc(100vw - ${sidebarMinWidth}px)`);

    useEffect(() => {
      const calculateLayout = () => {
        const vwWidth = window.innerWidth * 0.05;
        const effectiveSidebarWidth = Math.max(vwWidth, sidebarMinWidth);
        const newLeftOffset = `${effectiveSidebarWidth}px`;
        setLeftOffset(newLeftOffset);
        setCalculatedWidth(`calc(100vw - ${newLeftOffset})`);
      };
  
      calculateLayout();
      window.addEventListener('resize', calculateLayout);
      return () => window.removeEventListener('resize', calculateLayout);
    }, [sidebarMinWidth]);
    // ---

    const colors = useAppSelector((state) => state.theme.colors); 

    const isDropdownOpened = useAppSelector((state) => state.fileExplorer.isDropdownOpened); 
    const selectedFile = useAppSelector((state) => state.fileExplorer.selectedFile);
    const selectedFolder = useAppSelector((state) => state.fileExplorer.selectedFolder); 
    const currentFolder = useAppSelector((state) => state.fileExplorer.currentFolder); 
    const directory = useAppSelector((state) => state.fileExplorer.directory); 
    const mode = useAppSelector((state) => state.fileExplorer.mode); 

    // -- 

    const handleClick = (e: any) => { 
        // Prevent closing dropdown if click is inside it (basic check)
        const dropdownElement = document.getElementById('general-dropdown');
        if (dropdownElement && dropdownElement.contains(e.target as Node)) {
            return;
        }
        if (isDropdownOpened) { 
            dispatch(setIsDropdownOpened(false)); 
        }; 
    }; 
    
    // --

    const handleClickOptions = (e: any) => { 
        
        // Allow default context menu on mobile for now (easier than replicating dropdown)
        // if (isMobile) return; 

        if (mode === 'moveFile' || mode === 'sharedFolder') { 
            return; 
        };

        // Only trigger on right-click (button 2)
        if (e.button == 2) { 

            // Prevent default browser context menu
            e.preventDefault(); 

            if (isDropdownOpened && (selectedFolder || selectedFile)) { 
                dispatch(selectFile(null)); 
                dispatch(selectFolder(null)); 
    
            } else if (isDropdownOpened) { 
                dispatch(setIsDropdownOpened(false)); 

            } else { 
                // Don't show general dropdown if an item is selected or in shared folder view
                if (selectedFile || selectedFolder || directory?.includes('Shared')) { 
                    dispatch(setIsDropdownOpened(false)); 
                } else { 
                    dispatch(setMouseInfo({ clientX: e.clientX, clientY: e.clientY }));
                    dispatch(setIsDropdownOpened(true)); 
                }; 
            }; 
        }; 
    }; 

    // Prevent context menu default action specifically on the explorer background
    const handleContextMenu = (e: React.MouseEvent) => {
        // Allow context menu on inputs, textareas etc.
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return;
        }
        // Trigger our custom logic only on right-click
        if (e.button === 2) {
             e.preventDefault();
             handleClickOptions(e); // Call the existing logic
        }
    };


    // -- 

    return (

        <Box 
            id='fileExplorer'
            onClick={handleClick}
            onContextMenu={handleContextMenu} // Use onContextMenu
            // onMouseDown={(e) => handleClickOptions(e)} // Keep for potential left-click interactions if needed later
            sx={[{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', // Column on mobile, row on desktop
                alignItems: 'stretch', // Stretch items to fill cross-axis
                justifyContent: 'flex-start', // Start content from top/left
                width: calculatedWidth, // Use calculated width
                height: 'calc(100vh - 5vh)', // Use calculated height (below navbar)
                overflow: 'hidden', // Hide overflow on main container
                scrollbarWidth: 'none',
                position: 'absolute',
                top: '5vh', // Position below navbar
                left: leftOffset, // Use calculated offset
                bottom: 0,
                backgroundColor: colors?.interface,
                transition: 'left 0.2s ease-out, width 0.2s ease-out', // Add transition
            }, extraStyle ]}>

            {/* Sections */}
            {/* Pass isMobile to children that need to adapt layout */}
            <NavigationColumn isMobile={isMobile} /> 

            {/* Conditional rendering based on mode */}
            {((mode !== 'consultations') && (mode !== 'trash')) && <FilesSection isMobile={isMobile} />}
            { (mode === 'consultations') && <Consultations isMobile={isMobile} />}
            {(mode === 'trash') && <Trash isMobile={isMobile} />}

            {/* Dropdown - Positioning might still be suboptimal on mobile */}
            { (isDropdownOpened && !selectedFile && !selectedFolder) && <GeneralDropdown />}

            {/* { mode === 'correctFile' && <CorrectFile />} */}

        </Box>

    );

};

export default FileExplorer;